import { adoptSilentWrites, bindFormReset, reportIdCollision } from "@modyra/widgets";
import { applyDynamicRules, parseDynamicForm, assertLayoutWithinDepth, assertSafeDynamicFieldNames, vanillaReactivity } from "@modyra/core";
import { buildForm } from "./schema.js";
import { formErrorsOf, formScopeOf, idSafeKey, isValidWidgetId, layoutNodeAttributes, layoutSlotStyle, MDY_FORM_SHELL_CLASSES, MDY_ID_DELIMITER, MDY_LAYOUT_CLASSES } from "@modyra/widgets";
import { renderField } from "./fields/index.js";
import { el, setText } from "./dom.js";
/**
 * Joins a prefix to a field name to form a widget id.
 *
 * A single character that neither part may contain, which is what makes two distinct prefixes
 * provably unable to collide: the joiner's first occurrence always ends the prefix, so
 * `p1 + name1 === p2 + name2` forces `p1 === p2`. Were the prefix allowed to contain it,
 * `"a" + "b-c"` and `"a-b" + "c"` would be the same id.
 */
const ID_PREFIX_JOINER = "-";
/** Renders a complete form for `fields` into `container`. `container` is cleared first — this function owns everything inside it until `dispose()`. */
/**
 * A field name is an identity, and the typed entry point has to hold that precondition as firmly
 * as the dynamic parser does. The names themselves are core's rule — it owns what a schema key may
 * be — and are checked here rather than inside `buildForm` so a rejected list leaves the container
 * untouched instead of cleared.
 *
 * The prefix is this function's own: it exists only because a host may mount two forms built from
 * the same names, and nothing below this layer knows it was applied.
 */
function assertMountableNames(fields, idPrefix) {
    if (idPrefix !== undefined) {
        if (!isValidWidgetId(idPrefix)) {
            throw new Error(`mountMdyForm: idPrefix "${idPrefix}" must be non-empty and cannot contain "${MDY_ID_DELIMITER}" — ` +
                `it separates the segments of a generated id.`);
        }
        if (idPrefix.includes(ID_PREFIX_JOINER)) {
            throw new Error(`mountMdyForm: idPrefix "${idPrefix}" cannot contain "${ID_PREFIX_JOINER}" — it joins the prefix to ` +
                `the field name, and a prefix carrying it would let two different prefixes produce the same id.`);
        }
    }
    assertSafeDynamicFieldNames(fields);
}
export function mountMdyForm(container, fields, options = {}) {
    assertMountableNames(fields, options.idPrefix);
    // The same limit the document reader applies, at the door a document never passes through. A
    // structure assembled in code used to nest as deep as it liked, so the cap read as a rule about
    // the file format rather than about the form — and one form was legal or not depending on how it
    // had been written down.
    assertLayoutWithinDepth(options.layout);
    container.replaceChildren();
    container.classList.add("mdy-dynamic-form");
    // The form's own refusals, first, before the fields — a summary a person has to scroll past their
    // whole form to find is one they do not read. A `status` rather than a field's error list: it
    // speaks for the form, it appears in answer to something the person did, and it is announced when
    // it arrives. Empty and hidden until there is something to say.
    const formErrors = el("ul", MDY_FORM_SHELL_CLASSES.formErrors);
    formErrors.setAttribute("role", "status");
    formErrors.hidden = true;
    container.appendChild(formErrors);
    const reactivity = vanillaReactivity();
    const form = buildForm(fields, reactivity, options.collections, {
        draft: options.draft,
        validations: options.validations,
        fieldOptions: options.fieldOptions,
    });
    // Applied to the form rather than to the markup: what a rule decides is whether the field is in
    // play, which is the form's word and reaches the payload as well as the page.
    if (options.rules && options.rules.length > 0)
        applyDynamicRules(form, options.rules);
    // The widget id is the identity a field's DOM is built from; the field name stays the data path.
    // The two differ by this form's scope, which it has whether or not the host named one: two forms
    // built from the same document would otherwise both claim `when__label`, and a reference from the
    // second resolves into the first. ADR 0146.
    /**
     * Whether a live form on this page already answers to `scope`.
     *
     * Every id a form publishes begins with its scope, so one element carrying one is enough. A form
     * that has been disposed took its elements with it, which is what lets a remount read as the same
     * document rather than as a new one.
     */
    const scopeIsLive = (scope) => (container.ownerDocument ?? document).querySelector(`[id^="${scope}${ID_PREFIX_JOINER}"]`) !== null;
    const scope = options.idPrefix ?? formScopeOf(form, scopeIsLive);
    // The name is a path — a document names a nested field `rows.0.name` — and the separator is a class
    // selector to a browser, so an id carrying it cannot be reached by the consumer it was published
    // for. Spelled in the character set an id may hold, as every other piece of data in one is
    // (ADR 0141).
    const widgetIdFor = (name) => `${scope}${ID_PREFIX_JOINER}${idSafeKey(name)}`; /**
     * The handle a name points at.
     *
     * A name in this list is a path — a flattened document names a nested field `shipping.city` — and
     * the handle tree has the shape the form has, so the name is walked rather than looked up.
     */
    const handleFor = (name) => {
        let node = form.f;
        const segments = name.split(".");
        for (let index = 0; index < segments.length; index += 1) {
            if (typeof node !== "object" || node === null)
                return undefined;
            const holder = node;
            const segment = segments[index];
            // A collection is walked the way it is addressed rather than as an object: an array's rows are
            // a list, a record's are reached by key, and neither answers to `f.lines["0"]`. Without this a
            // document's collection mounted no controls at all — the value was right and the screen empty.
            // The row goes back through this same loop, because a row may hold a collection of its own:
            // `orders.o1.lines.l1.sku` crosses two of them, and each is walked the way it is addressed.
            if (typeof holder.rows === "function" || typeof holder.cell === "function") {
                const rest = segments.slice(index + 1);
                if (typeof holder.cell === "function" && rest.length === 1) {
                    return holder.cell(segment, rest[0]);
                }
                const row = typeof holder.rows === "function"
                    ? holder.rows()[Number(segment)]
                    : holder.row?.(segment);
                if (rest.length === 0)
                    return row;
                node = row;
                continue;
            }
            node = holder[segment];
        }
        return node;
    };
    const disposers = [];
    const byName = new Map(fields.map((f) => [f.name, f]));
    const rendered = new Set();
    const renderOne = (target, name) => {
        const field = byName.get(name);
        const handle = handleFor(name);
        if (!field || !handle || rendered.has(name))
            return;
        rendered.add(name);
        disposers.push(renderField(target, field, handle, reactivity, widgetIdFor(name)));
        // Name the root so a host can find a field's DOM without depending on child order —
        // which stops holding once a layout row nests fields inside it.
        const root = target.lastElementChild;
        if (root instanceof HTMLElement) {
            root.dataset.mdyField = name;
            // Two forms built from one document claim one set of ids unless the host prefixes them. The
            // page still works for one of them, which is what makes it worth saying out loud.
            // After the effect that writes the ids: this renderer's parts take theirs from the projection
            // on the first pass, which is a microtask away, so asking now is asking before the field has
            // published anything to collide with.
            queueMicrotask(() => reportIdCollision(root, "Pass a different `idPrefix` when mounting each form."));
        }
    };
    /** A v3 slot names a field and says where it sits; a bare string is the same slot saying nothing. */
    const isSlot = (child) => typeof child === "object" && "ref" in child;
    /** What a child asks of the column it occupies — a slot and a section answer the same way. */
    const placementOf = (child) => {
        if (typeof child === "string")
            return undefined;
        if (isSlot(child))
            return child.at;
        return child.kind === "section" ? child.at : undefined;
    };
    const renderLayoutChild = (target, child) => {
        if (typeof child === "string")
            renderOne(target, child);
        else if (isSlot(child))
            renderOne(target, child.ref);
        else
            renderLayoutNode(target, child);
    };
    function renderLayoutNode(target, node) {
        // Classes and the column count come from `@modyra/widgets`, so a two-column row is the same row
        // whichever adapter drew it and whichever theme is loaded.
        const attributes = layoutNodeAttributes(node);
        if (node.kind === "section") {
            const section = el("fieldset", attributes.className);
            section.dataset.layoutId = node.id;
            if (node.label) {
                const legend = el("legend", MDY_LAYOUT_CLASSES.sectionLabel);
                setText(legend, node.label);
                section.appendChild(legend);
            }
            for (const child of node.children)
                renderLayoutChild(section, child);
            target.appendChild(section);
            return;
        }
        const row = el("div", attributes.className);
        row.dataset.layoutId = node.id;
        for (const [property, value] of Object.entries(attributes.style))
            row.style.setProperty(property, value);
        for (const column of node.columns) {
            const cell = el("div", MDY_LAYOUT_CLASSES.column);
            // The column is the grid item, so a placement is applied here rather than to anything inside
            // it — `layoutSlotStyle` explains why. A section carries one too, which is how a group in a
            // row is placed; either way the first child with something to say wins.
            const placement = column.map(placementOf).find((at) => at !== undefined);
            if (placement) {
                for (const [property, value] of Object.entries(layoutSlotStyle(placement)))
                    cell.style.setProperty(property, value);
            }
            for (const child of column)
                renderLayoutChild(cell, child);
            row.appendChild(cell);
        }
        target.appendChild(row);
    }
    // Layout nodes are spliced in at the position of their first member rather than hoisted to the
    // top: a two-column row built from fields 3 and 4 has to stay between fields 2 and 5, and a
    // layout that only arranges part of the form must leave the rest where the author put it.
    const layoutNodes = options.layout ?? [];
    const firstMemberOf = new Map();
    const claimed = new Set();
    const collectNames = (child, into) => {
        if (typeof child === "string")
            into.push(child);
        else if (isSlot(child))
            into.push(child.ref);
        else if (child.kind === "section")
            child.children.forEach((c) => collectNames(c, into));
        else
            child.columns.forEach((column) => column.forEach((c) => collectNames(c, into)));
    };
    for (const node of layoutNodes) {
        const names = [];
        collectNames(node, names);
        const anchor = names.find((name) => byName.has(name) && !claimed.has(name));
        if (anchor === undefined)
            continue;
        firstMemberOf.set(anchor, node);
        for (const name of names)
            claimed.add(name);
    }
    for (const f of fields) {
        const node = firstMemberOf.get(f.name);
        if (node)
            renderLayoutNode(container, node);
        else if (!claimed.has(f.name))
            renderOne(container, f.name);
    }
    let submitButton = null;
    if (options.submitLabel !== null) {
        submitButton = el("button");
        submitButton.type = "button";
        setText(submitButton, options.submitLabel ?? "Submit");
        submitButton.addEventListener("click", () => {
            void form.submit(async (value) => options.onSubmit?.(value));
        });
        container.appendChild(submitButton);
        const submitEffect = reactivity.effect(() => {
            if (submitButton)
                submitButton.disabled = !form.state.canSubmit();
        });
        disposers.push(() => submitEffect.destroy());
    }
    // Field-level refusals reach the person through the field that owns them; these are the ones with
    // no field to reach them through — a failed call, a service that is down — and without this the
    // engine held them and the page said nothing.
    const formErrorsEffect = reactivity.effect(() => {
        const shown = formErrorsOf(form.state.lastSubmitErrors());
        formErrors.replaceChildren(...shown.map((error) => {
            const item = el("li", MDY_FORM_SHELL_CLASSES.formErrorItem);
            setText(item, error.message);
            return item;
        }));
        formErrors.hidden = shown.length === 0;
    });
    disposers.push(() => formErrorsEffect.destroy());
    // A `<form>` around this one, with a button that says Cancel. That button is `type="reset"`, and
    // the browser's reset sets each box back to its `value` *attribute* — which this renderer never
    // writes, because it writes the property. So the boxes emptied and the model kept what was typed:
    // a person pressed Cancel, watched the field clear, and submitted the value they thought they had
    // discarded. Returning to the initial is what a reset means and what the button says.
    disposers.push(bindFormReset({ element: container, reset: () => { form.reset(); } }));
    // Values written into the boxes by something that never says so, told to the model.
    //
    // Session history restoration hands a person their typing back when they press Back, and autofill
    // puts an address into fields nobody touched. Both write the value property and announce nothing,
    // so the field showed one value while the form held another and a submit sent the second.
    disposers.push(adoptSilentWrites({ root: container }));
    function dispose() {
        for (const disposeField of disposers)
            disposeField();
        submitButton?.remove();
        form.deactivate();
        container.replaceChildren();
        container.classList.remove("mdy-dynamic-form");
    }
    return { form, reactivity, dispose };
}
/**
 * Mount a form from a document, in one act.
 *
 * `parseDynamicForm` reads a document and `mountMdyForm` draws fields — and between them sits
 * `applyDynamicRules`, which a caller has to remember. Forget it and everything still compiles, the
 * form still mounts, and the conditions the document declared simply never fire: the page looks
 * obeyed. Three steps where two of them are the interesting one is a sequence nobody gets wrong
 * twice, and everybody gets wrong once.
 *
 * **Strict, whatever the parser's default is.** A lenient parse returns what it could read and says
 * what it could not; that is the right default for a reader, and the wrong one for this door,
 * because the person using it is exactly the one who will not read the diagnostics. A document that
 * lost something is refused here, with what it lost.
 */
export function mountDynamicForm(container, document_, options = {}) {
    const parsed = parseDynamicForm(document_, { mode: "strict" });
    if (!parsed.ok) {
        const errors = parsed.diagnostics.filter((diagnostic) => diagnostic.severity === "error");
        throw new TypeError(`This document cannot be mounted:\n\n`
            + errors.map((each) => `    ${each.code} at ${each.path}\n      ${each.message}`).join("\n")
            + "\n\n  Mounted strictly: a document that lost a declaration is refused rather than drawn\n"
            + "  without it. `parseDynamicForm` reads leniently if that is what you want, and tells you\n"
            + "  what it dropped.");
    }
    return mountMdyForm(container, parsed.fields, {
        ...options,
        layout: parsed.layout,
        rules: parsed.rules,
        collections: parsed.collections,
    });
}
